// The huddle surfaces (ios/SourcesMac/MacHuddle.swift):
//  - `HuddlePanel`: the always-there card at the FOOT OF THE SIDEBAR — who is in, who is talking
//    (a glow, not a word), who has a hand up, the controls one click away (MacHuddlePanel);
//  - `HuddleWindow`: the expanded huddle — the Mac opens a window of its own; a browser tab has
//    one window, so it is an in-page panel over the main column (full screen at phone width). A
//    stage that SHOWS a shared screen, or everyone's face (or camera) large (MacHuddleWindowView).
import { useEffect } from 'react';
import { CallRoster } from '../../../core/callRoster';
import { t, tf } from '../../../i18n/i18n';
import { callManager, type CallUiState } from './callManager';
import { CallIcon } from './callIcons';
import { HuddleControls, HuddleFace, HuddleReactionsOverlay, personName, StreamVideo, useElapsed } from './HuddleParts';
import { useCallState } from './useCalls';
import { CallInviteOutcomeLine } from './CallInvite';
import { callInviter } from './callInvite';
import './calls.css';

const MAX_FACES = 6;

function raisedHandsLine(state: CallUiState): string | null {
  const hands = CallRoster.raisedHands(state.participants);
  if (!hands.length) return null;
  return tf('call.hands.raised', hands.map((p) => personName(state, p)).join(', '));
}

export function HuddlePanel() {
  const state = useCallState();
  const elapsed = useElapsed(state.phase === 'active' ? state.startAt : 0);
  if (state.phase === 'idle') return null;
  const people = state.participants;
  const hands = raisedHandsLine(state);
  return (
    <section className={`huddle-panel${state.phase === 'active' ? ' active' : ''}`} aria-label={state.channelName || t('call.window.title')} data-testid="huddle-panel">
      <header className="huddle-panel-head">
        <span className="huddle-headphones">
          <CallIcon name="headphones" size={13} strokeWidth={2.1} />
        </span>
        <span className="huddle-panel-name">{state.channelName || t('call.window.title')}</span>
        <span className="huddle-panel-clock">{state.phase === 'active' && state.startAt > 0 ? elapsed : t('call.connecting')}</span>
      </header>
      <div className="huddle-panel-faces">
        {people.slice(0, MAX_FACES).map((p) => (
          <HuddleFace key={p.sessionId} state={state} participant={p} size={30} reaction={[...state.reactions].reverse().find((r) => r.sessionId === p.sessionId)?.glyph ?? null} />
        ))}
        {people.length > MAX_FACES ? <span className="huddle-more">+{people.length - MAX_FACES}</span> : null}
        {state.phase === 'active' ? (
          <button type="button" className="huddle-invite-face" onClick={() => callInviter.openPicker()} title={t('web.call.invite.help')} aria-label={t('web.call.invite.help')} data-testid="call-invite">
            <CallIcon name="personAdd" size={14} strokeWidth={2} />
          </button>
        ) : null}
        {people.length <= 1 && state.phase === 'active' ? <span className="huddle-waiting">{t('call.waiting')}</span> : null}
      </div>
      {hands ? <div className="huddle-hands">{hands}</div> : null}
      <CallInviteOutcomeLine />
      <HuddleControls state={state} onExpand={() => callManager.setExpanded(true)} />
    </section>
  );
}

export function HuddleWindow() {
  const state = useCallState();
  const elapsed = useElapsed(state.startAt);
  useEffect(() => {
    if (!state.expanded) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !document.querySelector('.huddle-popover') && callManager.setExpanded(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.expanded]);
  if (!state.expanded || state.phase === 'idle') return null;
  const hands = raisedHandsLine(state);
  const sharer = state.participants.find((p) => p.sharingScreen);
  const videoFor = (sessionId: string, videoOn: boolean): MediaStream | null => {
    if (sessionId === state.sessionId) return state.cameraOn ? state.localVideo : null;
    return videoOn ? state.remoteVideos[sessionId] ?? null : null;
  };
  const anyVideo = state.participants.some((p) => videoFor(p.sessionId, p.videoOn));
  return (
    <div className="huddle-window" role="dialog" aria-label={state.channelName || t('call.window.title')} data-testid="huddle-window">
      <header className="huddle-window-head">
        <span className="huddle-headphones active">
          <CallIcon name="headphones" size={17} strokeWidth={2} />
        </span>
        <h2>{state.channelName || t('call.window.title')}</h2>
        {state.startAt > 0 && state.phase === 'active' ? <span className="huddle-panel-clock">{elapsed}</span> : <span className="huddle-panel-clock">{t('call.connecting')}</span>}
        <span className="huddle-controls-spacer" />
        {hands ? <span className="huddle-hands">{hands}</span> : null}
        <button type="button" className="icon-button" onClick={() => callManager.setExpanded(false)} title={t('call.collapse')} aria-label={t('call.collapse')}>
          <CallIcon name="collapse" size={17} strokeWidth={1.9} />
        </button>
      </header>
      {state.remoteScreen ? (
        <div className="huddle-stage screen">
          <div className="huddle-screen" data-testid="huddle-screen">
            <StreamVideo stream={state.remoteScreen} />
            {/* Over the PICTURE, never over the faces strip under it. */}
            <HuddleReactionsOverlay reactions={state.reactions} state={state} large />
            {sharer ? <span className="huddle-sharer">{tf('call.sharing.who', personName(state, sharer))}</span> : null}
          </div>
          <div className="huddle-strip">
            {state.participants.map((p) => (
              <HuddleFace key={p.sessionId} state={state} participant={p} size={40} showsName />
            ))}
          </div>
        </div>
      ) : (
        <div className="huddle-stage">
          <div className={`huddle-grid${anyVideo ? ' video' : ''}`}>
            {state.participants.map((p) => {
              const stream = videoFor(p.sessionId, p.videoOn);
              const isYou = p.sessionId === state.sessionId;
              return stream ? (
                <div key={p.sessionId} className={`huddle-tile video${p.speaking ? ' speaking' : ''}`} data-testid="huddle-video-tile">
                  <StreamVideo stream={stream} fill mirrored={isYou && callManager.selfViewMirrored} />
                  <span className="huddle-tile-name">
                    {p.muted ? <CallIcon name="micSlash" size={11} strokeWidth={2.4} /> : null}
                    {personName(state, p)}
                  </span>
                  {p.raisedHandAt > 0 ? <span className="huddle-tile-hand">✋</span> : null}
                </div>
              ) : (
                <div key={p.sessionId} className={`huddle-tile${p.speaking ? ' speaking' : ''}${anyVideo ? ' tall' : ''}`}>
                  <HuddleFace state={state} participant={p} size={88} showsName />
                </div>
              );
            })}
          </div>
          <HuddleReactionsOverlay reactions={state.reactions} state={state} large />
        </div>
      )}
      <footer className="huddle-window-controls">
        <CallInviteOutcomeLine />
        <HuddleControls state={state} large />
      </footer>
    </div>
  );
}
